"use client";

import { medalPoints } from "@dailywars/core/medals";
import { useState, type PointerEvent, type RefObject } from "react";

import { PlayerPreviewName } from "#/components/PlayerPreviewName";
import { cn } from "#/lib/utils";

import type { HiscoreRow } from "./-hiscores.ui";
import { useDraggableMarquee } from "./-index.monthlyHiscoresMarquee.scroll";

function MarqueeItem({
  entry,
  rank,
  onPreviewOpenChange,
  previewCloseSignal,
}: {
  entry: HiscoreRow;
  rank: number;
  onPreviewOpenChange: (open: boolean) => void;
  previewCloseSignal: number;
}) {
  return (
    <li className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs">
      <span className="text-muted-foreground">{rank}. </span>
      <PlayerPreviewName
        playerId={entry.playerId}
        displayName={entry.displayName}
        onOpenChange={onPreviewOpenChange}
        closeSignal={previewCloseSignal}
      />
      <span className="font-bold tabular-nums text-foreground">{medalPoints(entry)}p</span>
    </li>
  );
}

function MarqueeTrack({
  entries,
  ariaHidden,
  trackRef,
  onPreviewOpenChange,
  previewCloseSignal,
}: {
  entries: HiscoreRow[];
  ariaHidden?: boolean;
  trackRef?: RefObject<HTMLUListElement | null>;
  onPreviewOpenChange: (open: boolean) => void;
  previewCloseSignal: number;
}) {
  return (
    <ul
      ref={trackRef}
      className="flex items-center gap-8 px-4"
      aria-hidden={ariaHidden || undefined}
    >
      {entries.map((entry, index) => (
        <MarqueeItem
          key={`${ariaHidden ? "dup" : "main"}-${entry.playerId}`}
          entry={entry}
          rank={index + 1}
          onPreviewOpenChange={onPreviewOpenChange}
          previewCloseSignal={previewCloseSignal}
        />
      ))}
    </ul>
  );
}

function isPopoverUiTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    (target.closest("[data-slot='popover-trigger']") !== null ||
      target.closest("[data-slot='popover-content']") !== null)
  );
}

function useMarqueePreviewDismiss() {
  const [openPreviewCount, setOpenPreviewCount] = useState(0);
  const [previewCloseSignal, setPreviewCloseSignal] = useState(0);

  const dismissOpenPreviews = () => {
    if (openPreviewCount === 0) return;
    setPreviewCloseSignal((signal) => signal + 1);
    setOpenPreviewCount(0);
  };

  return {
    previewCloseSignal,
    popoverPaused: openPreviewCount > 0,
    dismissOpenPreviews,
    onPreviewOpenChange: (open: boolean) => {
      setOpenPreviewCount((count) => Math.max(0, count + (open ? 1 : -1)));
    },

    shouldHandleScrollerPointerDown: (target: EventTarget | null) => !isPopoverUiTarget(target),
  };
}

function marqueeScrollerClassName({
  overflows,
  isDragging,
}: {
  overflows: boolean;
  isDragging: boolean;
}) {
  return cn(
    "flex touch-pan-y select-none",
    overflows ? "w-max cursor-grab active:cursor-grabbing" : "w-full justify-center",
    isDragging && "cursor-grabbing",
  );
}

export function MarqueeViewport({ entries }: { entries: HiscoreRow[] }) {
  const preview = useMarqueePreviewDismiss();
  const durationSeconds = Math.max(20, entries.length * 4);
  const { viewportRef, trackRef, scrollerRef, overflows, isDragging, scrollerProps } =
    useDraggableMarquee({
      entryCount: entries.length,
      durationSeconds,
      popoverPaused: preview.popoverPaused,
      onDragStart: preview.dismissOpenPreviews,
    });

  const onScrollerPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!preview.shouldHandleScrollerPointerDown(event.target)) return;
    preview.dismissOpenPreviews();
    scrollerProps.onPointerDown(event);
  };

  return (
    <div
      ref={viewportRef}
      className={cn(
        "relative min-w-0 flex-1 overflow-hidden",
        overflows &&
          "mask-[linear-gradient(to_right,transparent,black_1rem,black_calc(100%-1rem),transparent)]",
      )}
    >
      <div
        ref={scrollerRef}
        className={marqueeScrollerClassName({ overflows, isDragging })}
        {...scrollerProps}
        onPointerDown={onScrollerPointerDown}
      >
        <MarqueeTrack
          entries={entries}
          trackRef={trackRef}
          onPreviewOpenChange={preview.onPreviewOpenChange}
          previewCloseSignal={preview.previewCloseSignal}
        />
        {overflows ? (
          <MarqueeTrack
            entries={entries}
            ariaHidden
            onPreviewOpenChange={preview.onPreviewOpenChange}
            previewCloseSignal={preview.previewCloseSignal}
          />
        ) : null}
      </div>
    </div>
  );
}
